'use client';

import Link from 'next/link';
import Image from 'next/image';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { cn, BLUR_DATA_URL } from '@/lib/utils';

interface AuthLayoutProps {
  /** Eyebrow above headline on the navy panel. */
  eyebrow?: string;
  /** Headline on the navy panel (Fraunces). */
  headline: React.ReactNode;
  /** Subheadline / description under the headline. */
  description?: string;
  /** Feature bullets shown below the description. */
  features?: Array<{ icon: React.ComponentType<{ className?: string }>; text: string }>;
  /** Optional cover image for the navy panel (object-cover). */
  imageSrc?: string;
  /** Right panel content (the form). */
  children: React.ReactNode;
  /** Right panel max-width. Defaults to `max-w-md`. */
  formMaxWidth?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * Two-column editorial auth layout: navy crest panel + cream form panel.
 * Mirrors the redesign navy + ochre + Fraunces aesthetic.
 */
export function AuthLayout({
  eyebrow,
  headline,
  description,
  features,
  imageSrc,
  children,
  formMaxWidth = 'md',
  className,
}: AuthLayoutProps) {
  const formWidth =
    formMaxWidth === 'sm'
      ? 'max-w-sm'
      : formMaxWidth === 'lg'
        ? 'max-w-lg'
        : 'max-w-md';

  return (
    <div className={cn('min-h-screen flex bg-surface-light', className)}>
      {/* ── Left editorial panel ─────────────────────────────────── */}
      <aside
        className="hidden lg:flex lg:w-[48%] xl:w-[52%] relative flex-col justify-between p-12 xl:p-14 bg-gradient-to-br from-[#faf7f2] via-[#f5f0e8] to-[#ede8de] text-ink overflow-hidden"
        aria-hidden="true"
      >
        {/* Top accent bar */}
        <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-accent via-primary/40 to-accent/30" />

        {/* Background image / zellige */}
        {imageSrc ? (
          <>
            <Image
              src={imageSrc}
              alt=""
              fill
              sizes="50vw"
              priority
              placeholder="blur"
              blurDataURL={BLUR_DATA_URL}
              className="object-cover opacity-[0.12]"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#faf7f2]/90 via-[#f5f0e8]/80 to-[#ede8de]/70" />
          </>
        ) : (
          <>
            <div className="absolute -top-24 -right-20 w-[26rem] h-[26rem] rounded-full bg-primary/[0.07] blur-3xl pointer-events-none" />
            <div className="absolute -bottom-32 -left-16 w-[28rem] h-[28rem] rounded-full bg-accent/[0.10] blur-3xl pointer-events-none" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[20rem] h-[20rem] rounded-full bg-accent/[0.05] blur-2xl pointer-events-none" />
          </>
        )}

        {/* Logo */}
        <Link href="/" className="relative z-10 inline-flex" aria-label="Accueil TERRAMEZ">
          <BrandLogo variant="dark" />
        </Link>

        {/* Main copy */}
        <div className="relative z-10 space-y-10 max-w-lg">
          {eyebrow && (
            <span className="eyebrow text-accent inline-block">{eyebrow}</span>
          )}
          <h2 className="font-display text-h2-lg xl:text-display leading-[1.05] tracking-tight text-ink">
            {headline}
          </h2>
          {description && (
            <p className="text-ink-secondary text-lg leading-relaxed">{description}</p>
          )}
          {features && features.length > 0 && (
            <ul className="space-y-4 pt-2">
              {features.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-3 text-sm text-ink-secondary">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-accent/10 border border-accent/20 shrink-0">
                    <Icon className="h-4 w-4 text-accent" aria-hidden />
                  </span>
                  <span className="pt-1.5">{text}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer caption */}
        <p className="relative z-10 text-xs text-ink-muted">
          © {new Date().getFullYear()} TERRAMEZ — Tous droits réservés
        </p>
      </aside>

      {/* ── Right form panel ──────────────────────────────────────────── */}
      <main className="flex-1 flex flex-col items-center justify-center bg-surface-light px-6 py-12 sm:px-12 lg:px-16">
        {/* Mobile logo */}
        <Link
          href="/"
          className="flex items-center gap-2 mb-10 lg:hidden"
          aria-label="Accueil TERRAMEZ"
        >
          <BrandLogo variant="dark" />
        </Link>

        <div className={cn('w-full', formWidth)}>{children}</div>
      </main>
    </div>
  );
}
